import { useEffect, useRef } from 'react';

import { useDebouncedCallback } from 'use-debounce';

import { useFormDataQuery } from '@/api/getters';
import { useFormMutation, useSubmitStepMutation } from '@/api/setters';
import { FormData, Stages, Steps } from '@/api/types';
import { isVehicleDamageProduct } from '@/components/helpers/productHelpers';
import { PRODUCT } from '@/consts';
import { formMutationDto } from '@/dto/formMutationDto';
import { useStepper } from '@/features/v2/lib/stepper';

import { useMutationState } from '@tanstack/react-query';
import { isEqual, sortBy } from 'lodash';
import { useFormContext } from 'react-hook-form';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const removeUndefinedValues = (obj: Record<any, any> | undefined) => {
  if (!obj) return {};
  const filteredEntries = Object.entries(obj).filter(
    ([_, v]) => v !== undefined
  );
  const sortedEntries = sortBy(filteredEntries, [0]);
  return Object.fromEntries(sortedEntries);
};

export const useMutationMiddleware = () => {
  const { watch } = useFormContext();
  const {
    step,
    innerStep,
    innerObject,
    currentStep,
    stepTag,
    setStepTag,
    isDeadEnd,
  } = useStepper();
  const { data } = useFormDataQuery();
  const mutation = useFormMutation();
  const isAfterFirstMutation = useRef(false);
  const isDeadEndRef = useRef(isDeadEnd);

  const submitStep = useSubmitStepMutation();
  const notAllowedStages = [
    Stages.STAGE_I_FINISHED,
    Stages.STAGE_I_DOCUMENTS,
    Stages.STAGE_II_FINISHED,
    Stages.STAGE_III_FINISHED,
    Stages.STAGE_IV_FINISHED,
    Stages.STAGE_V_FINISHED,
  ];

  const isUploader = !!currentStep?.subSteps;
  const submitStagesPending = useMutationState({
    filters: { status: 'pending' },
    select: (mutation) => {
      return mutation.meta?.mutationType === 'submitStages' &&
        mutation.state.variables === data?.stage
        ? true
        : false;
    },
  });

  const isSubmitStagesPending = submitStagesPending.some((item) => item);

  const debouncedMutate = useDebouncedCallback((value) => {
    if (
      (stepTag === Steps.START || stepTag === null) &&
      isVehicleDamageProduct(data?.product.id)
    ) {
      submitStep.mutateAsync(Steps.DAMAGE_VERIFICATION).then(() => {
        setStepTag(Steps.DAMAGE_VERIFICATION);
      });
    }

    if (data?.product.id === PRODUCT.HOUSE_HOLD && stepTag) {
      HHStepsHandler(data, stepTag, (step) => {
        submitStep.mutateAsync(step).then(() => {
          setStepTag(step);
        });
      });
    }

    if (!notAllowedStages.includes(value.stage) && !isSubmitStagesPending) {
      mutation.mutate({
        ...formMutationDto({
          form_data: value,
          stepper: {
            step,
            innerStep: { ...innerObject, [step]: innerStep },
          },
        }),
      });
    }
  }, 2000);

  useEffect(() => {
    isDeadEndRef.current = isDeadEnd;
    if (isDeadEnd) {
      debouncedMutate.cancel();
    }
  }, [isDeadEnd, debouncedMutate]);

  useEffect(() => {
    const subscription = watch((value) => {
      if (isDeadEndRef.current) return;
      if (
        ((!isEqual(
          removeUndefinedValues(data?.payload?.data),
          removeUndefinedValues(value)
        ) &&
          Object.keys(value).length > 0) ||
          isAfterFirstMutation.current) &&
        !isUploader
      ) {
        debouncedMutate(value);
        isAfterFirstMutation.current = true;
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, debouncedMutate, data?.payload?.data, isUploader]);
};

const HHStepsHandler = (
  data: FormData,
  currentStep: Steps,
  handleSubmit: (step: Steps) => void
) => {
  if (!data) return;

  const currentStage = data.stage;

  const currentStageKey = currentStage as keyof typeof stepStagesMapper;
  const stepsFromStage = stepStagesMapper[currentStageKey];
  const isStepInStage = stepsFromStage.includes(currentStep as Steps);

  if (!isStepInStage) {
    handleSubmit(stepsFromStage[0]);
  }
};

const stepStagesMapper = {
  [Stages.STAGE_I]: [Steps.Step_I_start, Steps.Step_I_finish],
  [Stages.STAGE_II]: [Steps.Step_II_start, Steps.Step_II_finish],
  [Stages.STAGE_III]: [Steps.Step_III_start, Steps.Step_III_finish],
  [Stages.OFFER]: [
    Steps.Offer_start,
    Steps.Offer_finished,
    Steps.Offer_rejected,
  ],
};
